import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { StepAnswer, TestTask } from '../api/tests';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { isAppPath } from './testSteps';
import {
  answerStep,
  dismissFinish,
  finishWebCard,
  flushNotes,
  goToStep,
  loadWebCards,
  myStep,
  noteStep,
  onStepPage,
  resetWebCards,
  selectWebCard,
  stepKey,
  useWebCards,
} from './webCards';

const STORED = 'ops.testPanel.collapsed';

/** Whether the panel was left folded; on a phone it starts folded, as a pill. A browser that keeps nothing still works. */
function readCollapsed(): boolean {
  try {
    const kept = window.localStorage.getItem(STORED);
    if (kept !== null) return kept === '1';
  } catch {
    // Private mode, storage off: the default below.
  }
  try {
    return window.matchMedia('(max-width: 899px)').matches;
  } catch {
    return false;
  }
}

function keepCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(STORED, collapsed ? '1' : '0');
  } catch {
    // Nothing kept: the panel opens as it does by default next time.
  }
}

/** The step a card opens on: the first the reader has not answered, the last when all are. */
function firstUnanswered(card: TestTask, wanted: Record<string, StepAnswer>): number {
  const steps = card.steps ?? [];
  const at = steps.findIndex((s) => !(wanted[stepKey(card.id, s.id)] ?? myStep(card, s.id).answer));
  return at < 0 ? Math.max(0, steps.length - 1) : at;
}

/**
 * «Prova en curs» (the owner, 2026-10-12): while the reader holds a web test
 * card, its current step follows them to every page, so the step is answered
 * on the page it is about. Docked at the bottom right on a desktop; on a phone
 * a pill above the bottom bar that opens a sheet. Not on the card's own page,
 * which already is all of this, and not for a visitor.
 */
export function TestPanel() {
  const t = ca.testPanel;
  const { me } = useAuth();
  const { pathname, search } = useLocation();
  const store = useWebCards();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = useRef<HTMLButtonElement>(null);

  const webCards = me.webCards ?? 0;
  useEffect(() => {
    if (!me.authenticated) {
      resetWebCards();
      return;
    }
    // Asked only when the account says it holds one: a page of anybody else costs no request.
    if (webCards > 0) void loadWebCards();
  }, [me.authenticated, me.id, webCards]);

  // What the last «Acaba la prova» said, when the card is no longer one the reader holds.
  const notice =
    store.finish && store.finish.status !== 'working' && store.finish.status !== 'failed'
      ? store.finish
      : null;
  const onOwnPage =
    store.cards.some((c) => pathname === `/proves/${c.id}`) ||
    (notice !== null && pathname === `/proves/${notice.cardId}`);
  const visible = me.authenticated && (store.cards.length > 0 || notice !== null) && !onOwnPage;
  const expanded = visible && (!collapsed || notice !== null);

  // On a phone the open sheet covers the page's foot: the page gets that much room under it.
  useEffect(() => {
    document.body.classList.toggle('test-panel-on', visible);
    document.body.classList.toggle('test-panel-open', expanded);
    return () => {
      document.body.classList.remove('test-panel-on', 'test-panel-open');
    };
  }, [visible, expanded]);

  if (!visible) return null;

  if (store.cards.length === 0 && notice) {
    return (
      <section className="test-panel" aria-label={t.title}>
        <div className="test-panel-head">
          <i className="bi bi-clipboard-check" aria-hidden="true" />
          <span className="test-panel-title">{t.title}</span>
          <span className="test-panel-count">{notice.title}</span>
        </div>
        <div className="test-panel-body" id="test-panel-body">
          <p
            className={`test-panel-result ${notice.status === 'done' ? 'ok' : 'warn'}`}
            role="status"
          >
            <i
              className={`bi ${notice.status === 'done' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`}
              aria-hidden="true"
            />{' '}
            {notice.text}
          </p>
          <p className="test-panel-nav">
            <Link className="button small" to={`/proves/${notice.cardId}`}>
              {t.card}
            </Link>
            <button type="button" className="small" onClick={dismissFinish}>
              {t.dismiss}
            </button>
          </p>
        </div>
      </section>
    );
  }

  const card = store.cards.find((c) => c.id === store.selected) ?? store.cards[0];
  const steps = card.steps ?? [];
  if (steps.length === 0) return null;
  const index = Math.min(
    steps.length - 1,
    Math.max(0, store.at[card.id] ?? firstUnanswered(card, store.wanted)),
  );
  const step = steps[index];
  const key = stepKey(card.id, step.id);
  const mine = myStep(card, step.id);
  const answer = store.wanted[key] ?? mine.answer;
  const note = store.drafts[key] ?? mine.note ?? '';
  const saving = store.saving[key];
  const allAnswered = steps.every(
    (s) => store.wanted[stepKey(card.id, s.id)] ?? myStep(card, s.id).answer,
  );
  // An answer still on its way to the platform is not yet one it would count.
  const unsent = steps.some((s) => stepKey(card.id, s.id) in store.wanted);
  const finish = store.finish?.cardId === card.id ? store.finish : null;
  const finishing = finish?.status === 'working';
  const elsewhere = isAppPath(step.url) && !onStepPage(step.url, pathname, search);

  const fold = (next: boolean) => {
    setCollapsed(next);
    keepCollapsed(next);
  };
  const go = (to: number) => {
    flushNotes();
    goToStep(card.id, to);
  };
  const choose = (value: StepAnswer) => {
    // The panel stays on the step just answered: its note is still to be written.
    goToStep(card.id, index);
    answerStep(card.id, step.id, value);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape' || collapsed) return;
    e.stopPropagation();
    flushNotes();
    fold(true);
    toggle.current?.focus();
  };

  const choices: [StepAnswer, string, string][] = [
    ['yes', t.yes, 'bi-check-lg'],
    ['no', t.no, 'bi-x-lg'],
    ['cant', t.cant, 'bi-question-lg'],
  ];

  return (
    <section
      className={`test-panel${collapsed ? ' collapsed' : ''}`}
      aria-label={t.title}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="test-panel-head"
        ref={toggle}
        aria-expanded={!collapsed}
        aria-controls="test-panel-body"
        title={collapsed ? t.expand : t.collapse}
        onClick={() => fold(!collapsed)}
      >
        <i className="bi bi-clipboard-check" aria-hidden="true" />
        <span className="test-panel-title">{t.title}</span>
        <span className="test-panel-count">{t.stepOf(index + 1, steps.length)}</span>
        <i className={`bi bi-chevron-${collapsed ? 'up' : 'down'}`} aria-hidden="true" />
      </button>
      {!collapsed && (
        <div className="test-panel-body" id="test-panel-body">
          {store.cards.length > 1 ? (
            <label className="test-panel-switch">
              <span className="vh">{t.switcher}</span>
              <select
                className="control"
                value={card.id}
                onChange={(e) => {
                  flushNotes();
                  selectWebCard(Number(e.target.value));
                }}
              >
                {store.cards.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="test-panel-card">{card.title}</p>
          )}
          <p className="test-panel-step">{step.text}</p>
          {elsewhere && step.url && (
            <p className="test-panel-open">
              <Link className="button small" to={step.url}>
                <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> {t.open}
              </Link>
            </p>
          )}
          <div className="test-panel-answers" role="group" aria-label={t.answers}>
            {choices.map(([value, label, icon]) => (
              <button
                key={value}
                type="button"
                className={`small answer-${value}`}
                aria-pressed={answer === value}
                onClick={() => choose(value)}
              >
                <i className={`bi ${icon}`} aria-hidden="true" /> {label}
              </button>
            ))}
          </div>
          <label className="test-panel-note">
            <span className="vh">{t.note}</span>
            <input
              type="text"
              className="control"
              value={note}
              maxLength={2000}
              placeholder={t.notePlaceholder}
              onChange={(e) => {
                goToStep(card.id, index);
                noteStep(card.id, step.id, e.target.value);
              }}
              onBlur={flushNotes}
            />
          </label>
          <div className="test-panel-nav">
            <button
              type="button"
              className="small"
              disabled={index === 0}
              onClick={() => go(index - 1)}
            >
              <i className="bi bi-chevron-left" aria-hidden="true" /> {t.previous}
            </button>
            <button
              type="button"
              className="small"
              disabled={index >= steps.length - 1}
              onClick={() => go(index + 1)}
            >
              {t.next} <i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
            <span
              className={`test-panel-save small ${saving === 'error' ? 'error' : 'muted'}`}
              role="status"
            >
              {saving === 'saving'
                ? t.saving
                : saving === 'saved'
                  ? t.saved
                  : saving === 'error'
                    ? t.error
                    : ''}
            </span>
          </div>
          {allAnswered && (
            <div className="test-panel-finish">
              <p className="small test-panel-done">{t.allAnswered}</p>
              <button
                type="button"
                className="primary"
                disabled={finishing || unsent}
                onClick={() => void finishWebCard(card)}
              >
                <i className="bi bi-check2-circle" aria-hidden="true" />{' '}
                {finishing ? t.finishing : t.finish}
              </button>
              {finish?.status === 'failed' && (
                <p className="small test-panel-fail" role="alert">
                  {finish.text}
                </p>
              )}
            </div>
          )}
          <p className="test-panel-link small">
            <Link to={`/proves/${card.id}`}>{t.card}</Link>
          </p>
        </div>
      )}
    </section>
  );
}
